import React, { useEffect, useState } from 'react';
import { Button, FormWithRedirect, SaveButton, TextInput, required, useNotify } from 'react-admin';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DownloadIcon from '@material-ui/icons/GetApp';
import IconCancel from '@material-ui/icons/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import IconButton from '@mui/material/IconButton';
import { useHistory } from 'react-router-dom';

import { apiUrl } from './../common/constants';
import { headersAPIObjectAuth } from './../common/utils';
import { useTranslate } from './../common/language';

const formatAmount = amount => new Intl.NumberFormat('ca-ES', {
  style: 'currency',
  currency: 'EUR'
}).format(amount || 0);

export const DonationsModel182 = props => {
  const translate = useTranslate();
  const notify = useNotify();
  const history = useHistory();
  const year = parseInt(props.match.params.year, 10);
  const [summary, setSummary] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [openGenerate, setOpenGenerate] = useState(false);
  const [summaryVersion, setSummaryVersion] = useState(0);

  useEffect(() => {
    let mounted = true;
    const loadSummary = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const query = new URLSearchParams({
          associations_id: localStorage.getItem('associations_id'),
          year: year.toString()
        });
        const response = await fetch(`${apiUrl}/donations_certificates/model-182/summary?${query}`, {
          headers: headersAPIObjectAuth()
        });
        if (!response.ok) {
          let message = translate('donations_model_182.summary_error');
          try {
            const responseError = await response.json();
            if (responseError.message) message = responseError.message;
          } catch (ignore) {}
          throw new Error(message);
        }
        const data = await response.json();
        if (mounted) setSummary(data);
      } catch (loadError) {
        if (mounted) setError(loadError.message || translate('donations_model_182.summary_error'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadSummary();
    return () => { mounted = false; };
  }, [year, translate, summaryVersion]);

  const validateReceiptNumber = value => value && !/^182(?!0{10}$)\d{10}$/.test(value)
    ? translate('donations_model_182.receipt_number_invalid') : undefined;
  const validateContactPhone = value => value && !/^\d{9}$/.test(value)
    ? translate('donations_model_182.contact_phone_invalid') : undefined;

  const downloadModel182 = async values => {
    try {
      const response = await fetch(`${apiUrl}/donations_certificates/model-182/download`, {
        method: 'POST',
        headers: headersAPIObjectAuth(),
        body: JSON.stringify({
          associations_id: parseInt(localStorage.getItem('associations_id'), 10),
          year,
          receipt_number: values.receipt_number,
          contact_name: values.contact_name,
          contact_phone: values.contact_phone
        })
      });
      if (!response.ok) {
        let message = translate('donations_model_182.generate_error');
        try {
          const responseError = await response.json();
          if (responseError.message) message = responseError.message;
        } catch (ignore) {}
        throw new Error(message);
      }

      const blob = await response.blob();
      const href = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const tempLink = document.createElement('a');
      tempLink.href = href;
      tempLink.setAttribute('download', fileNameMatch
        ? fileNameMatch[1]
        : `${translate('donations_model_182.file_name_prefix')}-182-${values.receipt_number}.txt`);
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(href);
      setOpenGenerate(false);
      setSummaryVersion(version => version + 1);
      notify(translate('donations_model_182.generate_success'), 'success');
    } catch (downloadError) {
      notify(downloadError.message || translate('donations_model_182.generate_error'), 'error');
    }
  };

  if (loading) return <Box sx={{ mt: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" className="mt-20">{error}</Typography>;

  const generationErrors = summary.generation_errors || [];
  const canGenerate = summary.included.length > 0 && generationErrors.length === 0;

  return <>
    <Typography variant="h5" className="grey8 mt-20 mb-20">
      {translate('donations_model_182.title')} {year}
    </Typography>

    <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button
        label={translate('donations_model_182.generate_button')}
        disabled={!canGenerate}
        onClick={() => setOpenGenerate(true)}
      >
        <DownloadIcon />
      </Button>
      <Button
        label={translate('donations_model_182.configure_entity_fiscality')}
        onClick={() => history.push('/settings-data#model-182-fiscality')}
      >
        <SettingsIcon />
      </Button>
    </Box>

    <Model182FiscalSummary summary={summary} translate={translate} />

    {generationErrors.map(item =>
      <Typography key={item} color="error" className="mb-10">
        {translate(`donations_model_182.generation_error_${item.toLowerCase()}`)}
      </Typography>
    )}

    <Model182Table
      title={translate('donations_model_182.included_title')}
      description={translate('donations_model_182.included_description')}
      rows={summary.included}
      total={summary.included_total}
      showReason={false}
      translate={translate}
    />

    <Model182Table
      title={translate('donations_model_182.excluded_title')}
      description={translate('donations_model_182.excluded_description')}
      rows={summary.excluded}
      total={summary.excluded_total}
      showReason
      translate={translate}
    />

    <Dialog open={openGenerate} onClose={() => setOpenGenerate(false)}
      aria-label={translate('donations_model_182.generate_button')}>
      <DialogTitle>{translate('donations_model_182.generate_button')}</DialogTitle>
      <FormWithRedirect
        save={downloadModel182}
        initialValues={{
          receipt_number: summary.receipt_number || '',
          contact_name: summary.contact_name || '',
          contact_phone: summary.contact_phone || ''
        }}
        render={({ handleSubmitWithRedirect, pristine, saving }) => <>
          <DialogContent>
            <TextInput fullWidth source="receipt_number"
              label={translate('donations_model_182.receipt_number')}
              validate={[required(), validateReceiptNumber]} />
            <TextInput fullWidth source="contact_phone"
              label={translate('donations_model_182.contact_phone')}
              validate={[required(), validateContactPhone]} />
            <TextInput fullWidth source="contact_name"
              label={translate('donations_model_182.contact_name')}
              validate={[required()]} />
          </DialogContent>
          <DialogActions>
            <Button label="ra.action.cancel" onClick={() => setOpenGenerate(false)}><IconCancel /></Button>
            <SaveButton handleSubmitWithRedirect={handleSubmitWithRedirect} pristine={pristine}
              saving={saving} disabled={false}
              label={translate('donations_model_182.generate_button')} icon={<DownloadIcon />} />
          </DialogActions>
        </>}
      />
    </Dialog>
  </>;
};

const Model182Table = ({ title, description, rows, total, showReason, translate }) => {
  const [detail, setDetail] = useState();
  const columns = showReason ? 8 : 6;
  return <Box sx={{ mb: 3 }}>
  <Typography variant="h6">{title}</Typography>
  <Typography sx={{ mb: 1 }}>{description}</Typography>
  <TableContainer component={Paper}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{translate('donations_model_182.nif')}</TableCell>
          <TableCell>{translate('donations_model_182.donor')}</TableCell>
          <TableCell>{translate('donations_model_182.deduction_key')}</TableCell>
          {showReason ? <TableCell>{translate('donations_model_182.exclusion_reason')}</TableCell> : null}
          <TableCell>{translate('donations_model_182.recurrence')}</TableCell>
          <TableCell align="right">{translate('donations_model_182.amount')}</TableCell>
          <TableCell align="center">{translate('donations_model_182.detail')}</TableCell>
          {showReason ? <TableCell /> : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? <TableRow>
          <TableCell colSpan={columns}>{translate('donations_model_182.no_data')}</TableCell>
        </TableRow> : rows.map((row, index) => <TableRow key={`${row.nif}-${index}`}>
          <TableCell>{row.nif || '-'}</TableCell>
          <TableCell>{row.name || '-'}</TableCell>
          <TableCell>{row.deduction_key || '-'}</TableCell>
          {showReason ? <TableCell>{translate(`donations_model_182.reason_${row.exclusion_reason.toLowerCase()}`)}</TableCell> : null}
          <TableCell>{translate(row.recurring
            ? 'donations_model_182.recurring_yes' : 'donations_model_182.recurring_no')}</TableCell>
          <TableCell align="right">{formatAmount(row.amount)}</TableCell>
          <TableCell align="center">
            <IconButton size="small" onClick={() => setDetail(row)}
              title={translate('donations_model_182.open_detail')}
              aria-label={translate('donations_model_182.open_detail')}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </TableCell>
          {showReason ? <TableCell>
            {row.users_id ? <Button label=""
              title={translate('donations_model_182.open_user')}
              onClick={() => window.open(`/#/users/${row.users_id}`, '_blank')}
              startIcon={<PersonIcon />} />
              : row.donations_user_id && row.donations_project_id ? <Button label=""
                title={translate('donations_model_182.edit_donation')}
                onClick={() => window.open(
                  `/#/reports/donations_users/${row.donations_project_id}?edit=${row.donations_user_id}&model182=1`,
                  '_blank')}
                startIcon={<EditIcon />} /> : null}
          </TableCell> : null}
        </TableRow>)}
        <TableRow>
          <TableCell colSpan={showReason ? 5 : 4}><strong>{translate('donations_model_182.total')}</strong></TableCell>
          <TableCell align="right"><strong>{formatAmount(total)}</strong></TableCell>
          <TableCell />
          {showReason ? <TableCell /> : null}
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
  <Model182DetailDialog row={detail} onClose={() => setDetail(undefined)} translate={translate} />
</Box>;
};

const formatDate = value => value ? new Intl.DateTimeFormat().format(new Date(value)) : '-';

const Model182DetailDialog = ({ row, onClose, translate }) => {
  if (!row) return null;
  const yearlyIncome = row.yearly_income || [];
  const tranches = row.applied_tranches || [];
  const criterionKey = (row.recurring_criterion || 'not_evaluated').toLowerCase();
  const donorTitle = row.name
    ? `${row.name}${row.nif ? ` (${row.nif})` : ''}`
    : row.nif || '-';
  return <Dialog open onClose={onClose} maxWidth="md" fullWidth
    aria-label={translate('donations_model_182.detail_title')}>
    <DialogTitle>{translate('donations_model_182.detail_title')}: {donorTitle}</DialogTitle>
    <DialogContent>
      <Typography variant="subtitle1">{translate('donations_model_182.recurrence')}</Typography>
      <Typography sx={{ mb: 2 }}>
        <strong>{translate(row.recurring
          ? 'donations_model_182.recurring_yes' : 'donations_model_182.recurring_no')}</strong>
        {' — '}{translate(`donations_model_182.recurrence_${criterionKey}`)}
      </Typography>

      <Typography variant="subtitle1">{translate('donations_model_182.income_history')}</Typography>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{translate('donations_model_182.year')}</TableCell>
            <TableCell>{translate('donations_model_182.source')}</TableCell>
            <TableCell>{translate('donations_model_182.date')}</TableCell>
            <TableCell>{translate('donations_model_182.concept')}</TableCell>
            <TableCell align="right">{translate('donations_model_182.amount')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {yearlyIncome.map(income => {
              const sourceItems = income.source_items || [];
              return sourceItems.length > 0 ? sourceItems.map((item, index) => <TableRow
                key={`${income.year}-${item.source}-${item.id}-${index}`}>
                {index === 0 ? <TableCell rowSpan={sourceItems.length}>
                  <strong>{income.year}</strong><br />{formatAmount(income.amount)}
                </TableCell> : null}
                <TableCell>{translate(`donations_model_182.source_${item.source.toLowerCase()}`)}</TableCell>
                <TableCell>{formatDate(item.date)}</TableCell>
                <TableCell>{item.project_name || item.concept || '-'}</TableCell>
                <TableCell align="right">{formatAmount(item.amount)}</TableCell>
              </TableRow>) : <TableRow key={income.year}>
                <TableCell><strong>{income.year}</strong></TableCell>
                <TableCell colSpan={3}>{translate('donations_model_182.no_income_found')}</TableCell>
                <TableCell align="right">{formatAmount(income.amount)}</TableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1">{translate('donations_model_182.applied_tranches')}</Typography>
      {tranches.length > 0 ? <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{translate('donations_model_182.tranche')}</TableCell>
            <TableCell>{translate('donations_model_182.deduction_key')}</TableCell>
            <TableCell align="right">{translate('donations_model_182.amount')}</TableCell>
            <TableCell align="right">{translate('donations_model_182.percentage')}</TableCell>
            <TableCell align="right">{translate('donations_model_182.deduction_amount')}</TableCell>
          </TableRow></TableHead>
          <TableBody>{tranches.map((tranche, index) => <TableRow key={`${tranche.tranche}-${index}`}>
            <TableCell>{translate(`donations_model_182.applied_tranche_${tranche.tranche.toLowerCase()}`)}</TableCell>
            <TableCell>{tranche.deduction_key || '-'}</TableCell>
            <TableCell align="right">{formatAmount(tranche.amount)}</TableCell>
            <TableCell align="right">{Number(tranche.percentage).toLocaleString(undefined,
              { maximumFractionDigits: 2 })} %</TableCell>
            <TableCell align="right">{formatAmount(tranche.deduction_amount)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </TableContainer> : <Typography>{translate('donations_model_182.no_applied_tranches')}</Typography>}
    </DialogContent>
    <DialogActions>
      <Button label="ra.action.close" onClick={onClose}><IconCancel /></Button>
    </DialogActions>
  </Dialog>;
};

const Model182FiscalSummary = ({ summary, translate }) => {
  const firstTranche = summary.individual_first_tranche_amount;
  const rules = summary.applied_percentages || [];
  const autonomousDeductions = summary.autonomous_deductions || [];
  const trancheLabel = tranche => {
    if (tranche === 'FIRST_TRANCHE') return translate('donations_model_182.tranche_first').replace('%amount%', firstTranche);
    if (tranche === 'REMAINDER') return translate('donations_model_182.tranche_remainder').replace('%amount%', firstTranche);
    if (tranche === 'AFFILIATION') return translate('donations_model_182.tranche_affiliation');
    return translate('donations_model_182.tranche_full');
  };
  const recurrenceLabel = value => value == null ? '-'
    : translate(value ? 'donations_model_182.recurring_yes' : 'donations_model_182.recurring_no');

  return <Box sx={{ mb: 3 }}>
    <Typography variant="h6">{translate('donations_model_182.fiscal_data_title')}</Typography>
    <Typography sx={{ mb: 1 }}>{translate('donations_model_182.fiscal_data_description')}</Typography>
    <TableContainer component={Paper}>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell><strong>{translate('donations_model_182.declarant_nature')}</strong></TableCell>
            <TableCell>{summary.declarant_nature == null
              ? translate('settings.model_182_nature_none')
              : translate(`settings.model_182_nature_${summary.declarant_nature}`)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>{translate('donations_model_182.deduction_key')}</strong></TableCell>
            <TableCell>{summary.deduction_key
              ? summary.deduction_key === 'F/G'
                ? translate('donations_model_182.deduction_key_f_g')
                : `${summary.deduction_key} - ${translate(`donations_model_182.deduction_key_${summary.deduction_key.toLowerCase()}`)}`
              : '-'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
    {rules.length > 0 ? <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{translate('donations_model_182.donor_type')}</TableCell>
            <TableCell>{translate('donations_model_182.deduction_key')}</TableCell>
            <TableCell>{translate('donations_model_182.tranche')}</TableCell>
            <TableCell>{translate('donations_model_182.recurrence')}</TableCell>
            <TableCell align="right">{translate('donations_model_182.percentage')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rules.map((rule, index) => <TableRow key={`${rule.donor_type}-${rule.tranche}-${index}`}>
            <TableCell>{translate(`donations_model_182.donor_type_${rule.donor_type.toLowerCase()}`)}</TableCell>
            <TableCell>{rule.deduction_key || '-'}</TableCell>
            <TableCell>{trancheLabel(rule.tranche)}</TableCell>
            <TableCell>{recurrenceLabel(rule.recurring)}</TableCell>
            <TableCell align="right">{Number(rule.percentage).toLocaleString(undefined, { maximumFractionDigits: 2 })} %</TableCell>
          </TableRow>)}
        </TableBody>
      </Table>
    </TableContainer> : null}
    <Typography variant="subtitle1" sx={{ mt: 2 }}>
      {translate('donations_model_182.autonomous_deductions_title')}
    </Typography>
    {autonomousDeductions.length > 0 ? <TableContainer component={Paper} sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{translate('donations_model_182.autonomous_community')}</TableCell>
            <TableCell>{translate('donations_model_182.autonomous_deduction')}</TableCell>
            <TableCell align="right">{translate('donations_model_182.percentage')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {autonomousDeductions.map((deduction, index) =>
            <TableRow key={`${deduction.autonomous_community_code}-${index}`}>
              <TableCell>{`${deduction.autonomous_community_code} - ${deduction.autonomous_community_name}`}</TableCell>
              <TableCell>{deduction.name || '-'}</TableCell>
              <TableCell align="right">
                {Number(deduction.percentage).toLocaleString(undefined, { maximumFractionDigits: 2 })} %
              </TableCell>
            </TableRow>)}
        </TableBody>
      </Table>
    </TableContainer> : <Typography variant="body2" sx={{ mt: 1 }}>
      {translate('donations_model_182.autonomous_deductions_none')}
    </Typography>}
  </Box>;
};
